// Basic
import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
// Components
import Entity from '@/common-utils/components/renders/entity/Entity';
import PaymentsTriggers from './sections/PaymentsTrigger';
import ServicesAndAppsLock from './sections/ServicesAndAppsLock';
import PaymentTickets from './sections/PaymentTickets';
import AdditionalServices from './sections/AdditionalServices';
import { Separator } from '@/common-utils/components/ui/separator';
// Common
import customizationFields from '../../../fields/customizationFields';
import CUSTOMIZATION_URL_ACTIONS from '../../../enums/CustomizationUrlActions';
// Lang
import { nsCustomization } from '@/app/i18n/namespaces';
import compareArraysOfObjects from '@/common-utils/functions/compareArraysOfObjects';

/**
 * @param root0
 * @param root0.customization
 * @param root0.isLoading
 * @param root0.setCheckedOptions
 * @param root0.checkedOptions
 * @param root0.action
 * @param root0.isPackage
 * @param root0.setIsValidConfig
 * @param root0.setIsScreenSaver
 * @param root0.setIsDirtyConfig
 * @returns {JSX} Functionnalities
 */
function Functionnalities({
    customization,
    isLoading = false,
    setCheckedOptions,
    checkedOptions,
    action,
    isPackage,
    setIsValidConfig,
    setIsScreenSaver,
    setIsDirtyConfig
}) {

    // Constants
    const entityPrefix = 'details.functionnalities';
    const isDisabled = action === CUSTOMIZATION_URL_ACTIONS.SHOW || (isPackage && action !== CUSTOMIZATION_URL_ACTIONS.DUPLICATE);

    // State
    // -- checked options on each section
    const [selectedPaymentTriggers, setSelectedPaymentTriggers] = useState(
        customization?.mpfOptions
            ? customization.mpfOptions
            : [{ name: customizationFields.MPF_OPTIONS.AMOUNT_SCREEN_DISPLAY.key, value: null }]
    );
    const [checkedPaymentTickets, setCheckedPaymentTickets] = useState([]);
    const [checkedAdditionalServices, setCheckedAdditionalServices] = useState([]);
    const [checkedServicesAndApps, setCheckedServicesAndApps] = useState([]);
    // -- Check if some sepecific options are selected/checked
    const [isExternalTrigger, setIsExternalTrigger] = useState(false);

    // Hooks
    // -- Set global options set
    useEffect(() => {
        const allOptions = [
            ...selectedPaymentTriggers,
            ...checkedPaymentTickets,
            ...checkedAdditionalServices,
            ...checkedServicesAndApps
        ];

        // Remove duplicate options by name
        const uniqueOptions = Array.from(new Map(allOptions.map(item => [item.name, item])).values());

        setCheckedOptions(uniqueOptions);
    }, [
        selectedPaymentTriggers,
        checkedPaymentTickets,
        checkedAdditionalServices,
        checkedServicesAndApps
    ]);

    useEffect(() => {
        const allSelectedOptions = [
            ...selectedPaymentTriggers,
            ...checkedPaymentTickets,
            ...checkedAdditionalServices,
            ...checkedServicesAndApps
        ];

        const originalOptions = customization?.mpfOptions.filter((option) => option.name !== customizationFields.MPF_OPTIONS.FORCING_GATEWAY.key) || [];
        const hasSameCheckedOptions = compareArraysOfObjects(allSelectedOptions, originalOptions, 'name');

        setIsDirtyConfig(!hasSameCheckedOptions);
    }, [selectedPaymentTriggers, checkedPaymentTickets, checkedAdditionalServices, checkedServicesAndApps]);

    return (
        <div className='space-y-4'>
            <div className='p-4'>
                <h5>
                    <Entity entity={`${entityPrefix}.payment_triggers.title`} namespace={nsCustomization} />
                </h5>
                <Separator className='my-2' />
                <PaymentsTriggers
                    customization={customization}
                    isLoading={isLoading}
                    selectedPaymentTriggers={selectedPaymentTriggers}
                    setSelectedPaymentTriggers={setSelectedPaymentTriggers}
                    checkedOptions={checkedOptions}
                    setIsValidConfig={setIsValidConfig}
                    setIsScreenSaver={setIsScreenSaver}
                    setIsExternalTrigger={setIsExternalTrigger}
                    isDisabled={isDisabled}
                />
            </div>
            <div className='p-4'>
                <h5>
                    <Entity entity={`${entityPrefix}.lock.title`} namespace={nsCustomization} />
                </h5>
                <Separator className='my-2' />
                <ServicesAndAppsLock
                    customization={customization}
                    isLoading={isLoading}
                    checkedServicesAndApps={checkedServicesAndApps}
                    setCheckedServicesAndApps={setCheckedServicesAndApps}
                    checkedOptions={checkedOptions}
                    isDisabled={isDisabled}
                    isExternalTrigger={isExternalTrigger}
                />
            </div>
            <div className='p-4'>
                <h5>
                    <Entity entity={`${entityPrefix}.payment_tickets.title`} namespace={nsCustomization} />
                </h5>
                <Separator className='my-2' />
                <PaymentTickets
                    customization={customization}
                    isLoading={isLoading}
                    checkedPaymentTickets={checkedPaymentTickets}
                    setCheckedPaymentTickets={setCheckedPaymentTickets}
                    checkedOptions={checkedOptions}
                    isDisabled={isDisabled}
                />
            </div>
            <div className='p-4'>
                <h5>
                    <Entity entity={`${entityPrefix}.additional_services.title`} namespace={nsCustomization} />
                </h5>
                <Separator className='my-2' />
                <AdditionalServices
                    customization={customization}
                    isLoading={isLoading}
                    checkedAdditionalServices={checkedAdditionalServices}
                    setCheckedAdditionalServices={setCheckedAdditionalServices}
                    checkedOptions={checkedOptions}
                    isDisabled={isDisabled}
                />
            </div>
        </div>
    );
}

Functionnalities.propTypes = {
    customization: PropTypes.object,
    isLoading: PropTypes.bool,
    setCheckedOptions: PropTypes.func,
    checkedOptions: PropTypes.array,
    action: PropTypes.string,
    isPackage: PropTypes.bool,
    setIsValidConfig: PropTypes.func,
    setIsScreenSaver: PropTypes.func,
    setIsDirtyConfig: PropTypes.func
};
export default Functionnalities;